/**
 * This is the table view of a Program's Members and only appears on that page.
 * Uses a DataTable component to provide sorting, filtering, pagination and row selection
 *
 * @design https://projects.invisionapp.com/d/main#/console/15478629/463278858/preview
 *
 */
import React, { useState } from 'react';
import {
	map,
	isNull,
	values,
	head,
	sortBy,
} from 'lodash';
import cx from 'classnames';

import CmsContentList from 'components/data/CmsContentList.js';
import CmsContentRenderer from 'components/data/CmsContentRenderer.js';
import ServerDate from 'components/ServerDate.js';
import { ALT_DATE_FORMAT } from "utils/Constants.js";
import DataTable from 'components/DataTable.js';
import {
	NO_FILTERS_MEMBERS_B2B,
	STATUS_SORT_TYPE,
} from 'components/FiltersConstants.js';

import PublicAppVars from 'utils/PublicAppVars.js';
import Pagination from "components/Pagination.js";

import Button, { buttonTypeStylePlain } from 'components/Button.js';
import { useModalContext } from 'context/ModalProvider.js';
import { useProgramIdContext } from 'context/group-admin/ProgramIdContext.js';
import useProgramMembers from 'components/data/hooks/useProgramMembers.js';
import LoadingIcon from 'components/icons/LoadingIcon.js';
import { MemberStatus } from './MembersTableColumns.js';
import Panel from 'components/Panel.js';
import Select from 'components/forms/inputs/Select.js';
import { Plus } from 'components/Icon.js';

import * as tableStyle from 'components/DataTable.module.css';
import * as pagination from 'components/Pagination.module.css';
import * as style from './Members.module.css';
import CmsContentRendered from 'components/data/CmsContentRendered.js';



export const nameObjToString = ({ firstName = null, lastName = null }) => {
	return `${firstName ? firstName : ''} ${lastName ? lastName : ''}`;
};

const cms = {
	externalId: 'miscText.members-table-external-id',
	name: 'miscText.members-table-name',
	status: 'miscText.members-table-status',
	addedOn: 'miscText.members-table-added-on',
	expiration: 'miscText.members-table-expiration',
	riderClass: 'miscText.members-table-rider-class',
	productElection: 'miscText.members-table-product-election',
	multipleBenefits: 'miscText.members-table-multiple-benefits',
	transitId: 'miscText.members-table-transit-id',
	filterDescription: 'miscText.members-table-filter-description',
	perPageLabel: 'miscText.members-table-per-page-label',
	addButton: 'miscText.members-table-add-button',
	resetButton: 'miscText.members-table-reset-button',
	applyFilters: 'miscText.members-table-apply-filters',
};

const columns = (setModal, programId, nameColWidth) => ([
	{
		accessor: 'externalMemberId',
		Header: <CmsContentRenderer.Span contentKey={cms.externalId} fallbackValue="External ID" />,
		Cell: ({ value }) => <>
			{isNull(value) ? <>&mdash;</> : String(value)}
		</>,
	},
	{
		id: 'memberName',
		accessor: 'name',
		Header: <CmsContentRenderer.Span contentKey={cms.name} fallbackValue="Name" />,
		minWidth: 100,
		width: nameColWidth, // this value must be an Int
		maxWidth: 250,
		Cell: ({ value }) => <Button typeStyle={buttonTypeStylePlain} onClick={() => {}}>
			{isNull(value) ? <>&mdash;</> : nameObjToString(value)}
		</Button>,
	},
	{
		accessor: 'memberStatus',
		Header: <CmsContentRenderer.Span contentKey={cms.status} fallbackValue="Membership Status" />,
		Cell: ({ value }) => <MemberStatus status={value} />,
	},
	{
		accessor: 'activationDate',
		Header: <CmsContentRenderer.Span contentKey={cms.addedOn} fallbackValue="Member Added On" />,
		Cell: ({ value: expirationDate }) => <> {isNull(expirationDate)
			? <>&mdash;</>
			: <ServerDate
				dateISO={expirationDate}
				altFormat={ALT_DATE_FORMAT}
				options={{ month: 'short', day: '2-digit', year: 'numeric' }}
			/>}
		</>,
	},
	{
		accessor: 'expirationDate',
		Header: <CmsContentRenderer.Span contentKey={cms.expiration} fallbackValue="Membership Expiration" />,
		Cell: ({ value: expirationDate }) => <> {isNull(expirationDate)
			? <>&mdash;</>
			: <ServerDate
				dateISO={expirationDate}
				altFormat={ALT_DATE_FORMAT}
				options={{ month: 'short', day: '2-digit', year: 'numeric' }}
			/>}
		</>,
	},
	{
		accessor: 'riderClassName',
		Header: <CmsContentRenderer.Span contentKey={cms.riderClass} fallbackValue="Rider Class" />,
		Cell: ({ value: riderClassName }) => riderClassName ? <>{riderClassName} </> : <>&mdash;</>,
	},
	{
		accessor: 'benefitElections',
		Header: <CmsContentRenderer.Span contentKey={cms.productElection} fallbackValue="Product Election" />,
		Cell: ({ value: benefitElections }) => <>
			{(isNull(benefitElections) || !benefitElections.length)
				? <>&mdash;</>
				: benefitElections.length > 1
					? <CmsContentRenderer.Span contentKey={cms.multipleBenefits} fallbackValue="Multiple benefits" />
					: head(benefitElections).benefitName}
		</>,
	},
	{
		accessor: 'transitAccountId',
		Header: <CmsContentRenderer.Span contentKey={cms.transitId} fallbackValue="Transit ID" />,
	},
]);

const MembersTable = ({ programName }) => {
	const [ activePage, setActivePage ] = useState(1);
	const [ filtersApplied ] = useState(NO_FILTERS_MEMBERS_B2B);

	const programId = useProgramIdContext();

	const { programMembersResponse, loading } = useProgramMembers({
		programId,
		returnMemberCount: true,
		returnElections: true,
		filtersApplied,
		offset: (activePage - 1) * PublicAppVars.CHARGES_HISTORY_PAGE_SIZE,
	});

	// const { isMembersLocked } = useMembersLocked(programId);
	const isMembersLocked = false;

	const { setModal } = useModalContext();
	const members = map(programMembersResponse?.members, member => ({ ...member, allowMemberUpdates: programMembersResponse?.allowMemberUpdates })) || [];
	const [ membersList, setMembersList ] = useState(null);

	// Open BulkActions when more than 1 member is selected
	const onSelection = (selections) => {
		if (selections.length > 0) {
			// TODO
			// setModal(<BulkMemberActions {...{
			// 	selectedMembers: selections,
			// 	programId,
			// 	allowMemberUpdates: programMembersResponse?.allowMemberUpdates,
			// }} />);
		} else {
			return;
		}
	};

	const onClearSelections = () => {
		setModal(null);
	};

	const calcNameColWidth = () => {
		if (members.length) {
			const names = map(members, member => member?.name ? nameObjToString(member.name) : null);
			const longestName = sortBy(names, name => name ? -(name.length) : 0)[ 0 ];
			const colWidth = longestName?.length * 10;
			return colWidth > 150 ? colWidth : 150;
		}
		return 150;
	};

	return <CmsContentList list={values(cms)}>{({ cmsContent }) => (<>
		<Panel overrideClass={style.filtersPanel}>
			<CmsContentRendered.Div
				className={style.filterDescription}
				contentKey={cms.filterDescription}
				variables={{
					memberCount: programMembersResponse?.memberCount ?? 0,
					programName,
				}}
				fallbackValue={`Filter ${programMembersResponse?.memberCount ?? 0} Members of ${programName}`}
			/>
			<div className={style.perPageWrapper}>
				<CmsContentRenderer.Div
					className={style.perPageLabel}
					contentKey={cms.perPageLabel}
					fallbackValue="Per page"
				/>
				<Select
					name="perPage"
					defaultValue="25"
					options={[
						{ value: '25', label: "25" },
						{ value: '50', label: "50" },
						{ value: '75', label: "75" },
						{ value: '100', label: "100" },
					]}
					onChange={() => {}}
				/>
			</div>
		</Panel>

		<Panel overrideClass={style.filtersPanel}>
			<div className={style.filterButtonWrapper}>
				<Select
					name="filter"
					isSearchable={false}
					defaultValue="default"
					options={[
						{ value: "default", label: "Select filter" },
						{ value: 'filter1', label: "Filter #1" },
						{ value: 'filter2', label: "Filter #2" },
						{ value: 'filter3', label: "Filter #3" },
					]}
					onChange={() => {}}
					additionalClasses={style.filterSelect}
				/>
				<Button
					additionalClassNames={style.addButton}
					typeStyle={buttonTypeStylePlain}
					onClick={() => window.alert('Adding...')}
				>
					<div className={style.addIcon}>
						<Plus />
					</div>
					<CmsContentRenderer.Span
						contentKey={cms.addButton}
						fallbackValue="Add"
					/>
				</Button>
			</div>
			<div className={style.filterButtonWrapper}>
				<Button
					isPrimary={false}
					onClick={() => window.alert('Resetting...')}
				>
					<CmsContentRenderer.Span
						contentKey={cms.resetButton}
						fallbackValue="Reset"
					/>
				</Button>
				<Button onClick={() => window.alert('Applying...')}>
					<CmsContentRenderer.Span
						contentKey={cms.applyFilters}
						fallbackValue="Apply filters"
					/>
				</Button>
			</div>
		</Panel>

		<DataTable
			tableColumns={columns(setModal, programId, calcNameColWidth())}
			tableData={membersList || members}
			selectableRows
			disabledSelectables={isMembersLocked}
			isSortable
			canDownload={false}
			{...{ onSelection, onClearSelections }}
			getExpandableRowsCmsKey={() => {}}
			additionalTableClassNames={tableStyle.membersTable}
		/>

		{loading ? <LoadingIcon /> : null}
		{programMembersResponse?.memberCount > PublicAppVars.CHARGES_HISTORY_PAGE_SIZE
			? (
				<div className={cx(pagination.container)} role="cell">
					<Pagination
						totalCount={programMembersResponse.memberCount}
						itemsCountPerPage={PublicAppVars.CHARGES_HISTORY_PAGE_SIZE}
						activePage={activePage}
						setActivePage={setActivePage}

						// Range of pages in paginator, exclude navigation blocks (prev, next, first, last pages)
						pageRangeDisplayed={3}
					/>
				</div>
			) : null}

	</>)}</CmsContentList>;
};

MembersTable.propTypes = {};

export default MembersTable;
